import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {MenuItem} from 'primeng/api';
import {filter} from 'rxjs/operators';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {jobStatStatusLabelMap} from '../../interface/job-stat-menu.interface';
import {PageComponent} from '../../../../core/shared/mvs-page/page.component';
import {NavigationItem} from '../../../../core/shared/dto/navigation/navigation-item';
import {MvsCoreService} from '../../../../core/shared/service/mvs-core.service';
import {JbSpringBatchIndicatorService} from '../../service/jb-spring-batch-indicator.service';

@Component({
    selector: 'jb-job-dashboard',
    templateUrl: 'job-dashboard.page.html',
    styleUrls: ['job-dashboard.page.scss'],
    standalone: false
})
export class JobDashboardPage extends PageComponent implements OnInit, OnChanges, OnDestroy {

    defaultLabel: string = 'Job-Dashboard';

    objectServiceSubscription: Subscription;
    private lastExecutedNavAction: string;

    navigationItems: NavigationItem[] = [
        {
            label: 'Übersicht',
            action: 'overview',
            tooltip: 'Übersicht',
            icon: 'fa-solid fa-gauge-high',
            toggleable: false,
            default: true
        },
        {
            label: 'Jobs',
            action: 'jobTypes',
            tooltip: 'Job-Typen',
            icon: 'fa-solid fa-list-check',
            toggleable: false,
            default: false
        },
        {
            label: 'Job-Läufe',
            action: 'jobRuns',
            tooltip: 'Job-Läufe',
            icon: 'fa-solid fa-play',
            toggleable: false,
            default: false
        },
        {
            label: 'Spring Batch',
            action: 'springBatch',
            tooltip: 'Spring Batch',
            icon: 'fa-solid fa-gears',
            toggleable: false,
            default: false
        },
        {
            label: 'Überlappungen',
            action: 'overlap',
            tooltip: 'Überlappungen',
            icon: 'fa-solid fa-layer-group',
            toggleable: false,
            default: false
        },
        {
            label: 'Thread-Analyse',
            action: 'threadAnalysis',
            tooltip: 'Laufzeitverteilung nach Threads',
            icon: 'fa-solid fa-chart-pie',
            toggleable: false,
            default: false
        },
    ];

    private readonly navItemToPathMap: Record<string, string[]> = {
        overview: ['/jb/dashboard'],
        jobTypes: ['/jb/dashboard/jobs'],
        jobRuns: ['/jb/dashboard/runs'],
        springBatch: ['/jb/dashboard/spring-batch'],
        overlap: ['/jb/dashboard/overlap'],
        threadAnalysis: ['/jb/dashboard/thread-analysis']
    };

    constructor(
        protected route: ActivatedRoute,
        protected coreService: MvsCoreService,
        protected router: Router,
        protected indicatorService: JbSpringBatchIndicatorService) {
        super(route, coreService);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.initComponent();

        // Activate Spring Batch indicator in the topbar for all jobs
        this.indicatorService.activate({
            jobNamePattern: '%',
            pollIntervalMs: 10000,
            showStopButton: true
        });
    }

    /**
     * Initialize Component.
     */
    initComponent() {

        this.updateBreadcrumbFromRoute();

        this.objectServiceSubscription = this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                this.updateBreadcrumbFromRoute();
            });

        this.refreshComponent();
    }

    /**
     * Refresh Component.
     */
    refreshComponent() {
        this.initialized = true;
    }

    private updateBreadcrumbFromRoute(): void {
        const breadcrumbs: MenuItem[] = [];
        const action = this.activeNavigationItem?.action;

        switch (action) {
            case 'overview':
                breadcrumbs.push({
                    label: 'Übersicht',
                    routerLink: ['/jb/dashboard'],
                    queryParams: { navItem: 'overview' }
                });
                break;

            case 'jobTypes':
                breadcrumbs.push({
                    label: 'Job-Typen',
                    routerLink: ['/jb/dashboard/jobs'],
                    queryParams: { navItem: 'jobTypes' }
                });
                break;

            case 'jobRuns':
                breadcrumbs.push({
                    label: 'Job-Läufe',
                    routerLink: ['/jb/dashboard/runs'],
                    queryParams: { navItem: 'jobRuns' }
                });
                break;

            case 'springBatch':
                this.buildSpringBatchBreadcrumbs(breadcrumbs);
                break;

            case 'overlap':
                breadcrumbs.push({
                    label: 'Überlappungen',
                    routerLink: ['/jb/dashboard/overlap'],
                    queryParams: { navItem: 'overlap' }
                });
                break;

            case 'threadAnalysis':
                breadcrumbs.push({
                    label: 'Thread-Analyse',
                    routerLink: ['/jb/dashboard/thread-analysis'],
                    queryParams: { navItem: 'threadAnalysis' }
                });
                break;

            default:
                break;
        }

        this.setBreadcrumbItems(breadcrumbs);
    }
    private buildSpringBatchBreadcrumbs(breadcrumbs: MenuItem[]): void {
        breadcrumbs.push({
            label: 'Spring Batch',
            routerLink: ['/jb/dashboard/spring-batch'],
            queryParams: { navItem: 'springBatch' }
        });

        const childRoute = this.route.snapshot.firstChild;
        if (!childRoute) return;

        const { url, paramMap } = childRoute;
        const segments = url.map(s => s.path);
        const jobId = paramMap.get('jobId');
        const status = paramMap.get('status');

        if (segments.includes('job') && jobId) {
            breadcrumbs.push({
                label: `Job ${jobId}`,
                routerLink: ['/jb/dashboard/job', jobId]
            });
        }

        if (segments.includes('stat') && status) {
            const label = jobStatStatusLabelMap[status] ?? `Status: ${status}`;
            breadcrumbs.push({
                label,
                routerLink: ['/jb/dashboard/stat', status]
            });
        }
    }

    isNavComponent(): NavigationItem[] {
        return this.navigationItems;
    }

    override postNavigationSelection(): void {
        const navItem = this.activeNavigationItem;
        if (!navItem || navItem.action === this.lastExecutedNavAction) {
            return;
        }

        this.lastExecutedNavAction = navItem.action;

        const routePath = this.navItemToPathMap[navItem.action];

        if (this.router.url !== routePath.join('/')) {
            this.router.navigate(routePath, {
                queryParams: { navItem: navItem.action }
            });
        }
    }

    /**
     * Process changes within Binding.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {

        if (!this.initialized) {
            return;
        }

        if (changes['id']) {
            this.refreshComponent();
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
        super.ngOnDestroy();
        if (this.objectServiceSubscription) {
            this.objectServiceSubscription.unsubscribe();
        }
        // Deactivate Spring Batch indicator when leaving the dashboard
        this.indicatorService.deactivate();
    }
}
